import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { CheckCircle, XCircle, Trophy } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface Question {
  id: string;
  question: string;
  options: string[];
  correct_answer: number;
  explanation: string;
}

interface QuizPlayerProps {
  quizId: string;
  moduleId: string;
  userId: string;
  onComplete: (passed: boolean, score: number) => void;
}

const QuizPlayer: React.FC<QuizPlayerProps> = ({ quizId, moduleId, userId, onComplete }) => {
  const [quiz, setQuiz] = useState<any>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Map<number, number>>(new Map());
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadQuiz();
  }, [quizId]);

  const loadQuiz = async () => {
    const { data, error } = await supabase
      .from('quizzes')
      .select('*')
      .eq('id', quizId)
      .single();

    if (error) {
      toast({ title: 'Error loading quiz', variant: 'destructive' });
      return;
    }

    setQuiz(data);
    setQuestions((data.questions as unknown) as Question[]);
    setLoading(false);
  };

  const handleAnswerSelect = (questionIndex: number, answerIndex: number) => {
    const newAnswers = new Map(selectedAnswers);
    newAnswers.set(questionIndex, answerIndex);
    setSelectedAnswers(newAnswers);
  };

  const calculateScore = () => {
    let correct = 0;
    questions.forEach((question, index) => {
      const selected = selectedAnswers.get(index);
      if (selected === question.correct_answer) {
        correct++;
      }
    });
    return Math.round((correct / questions.length) * 100);
  };

  const handleSubmit = async () => {
    const finalScore = calculateScore();
    setScore(finalScore);
    setShowResults(true);

    const passed = finalScore >= (quiz.pass_mark || 80);

    // Save quiz attempt
    const { error } = await supabase
      .from('quiz_attempts')
      .insert({
        user_id: userId,
        quiz_id: quizId,
        module_id: moduleId,
        score: finalScore,
        passed,
        answers: Object.fromEntries(selectedAnswers),
      });

    if (error) {
      toast({ title: 'Error saving quiz attempt', variant: 'destructive' });
    } else {
      onComplete(passed, finalScore);
    }
  };

  const handleRetake = () => {
    setCurrentQuestion(0);
    setSelectedAnswers(new Map());
    setShowResults(false);
    setScore(0);
  };

  if (loading) {
    return <div className="text-center py-8">Loading quiz...</div>;
  }

  if (showResults) {
    const passed = score >= (quiz.pass_mark || 80);
    return (
      <Card className="p-8">
        <div className="text-center">
          {passed ? (
            <Trophy className="w-16 h-16 mx-auto mb-4 text-green-500" />
          ) : (
            <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          )}
          <h2 className="text-3xl font-bold mb-2">
            {passed ? 'Congratulations!' : 'Not quite there yet'}
          </h2>
          <p className="text-xl mb-4">Your score: {score}%</p>
          <p className="text-muted-foreground mb-6">
            Pass mark: {quiz.pass_mark || 80}%
          </p>

          {/* Show answers */}
          <div className="mt-8 text-left space-y-4">
            {questions.map((question, index) => {
              const selected = selectedAnswers.get(index);
              const isCorrect = selected === question.correct_answer;
              return (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-start gap-2 mb-2">
                    {isCorrect ? (
                      <CheckCircle className="w-5 h-5 text-green-500 mt-1" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500 mt-1" />
                    )}
                    <div className="flex-1">
                      <p className="font-semibold mb-2">{question.question}</p>
                      <p className="text-sm text-muted-foreground">
                        Your answer: {question.options[selected || 0]}
                      </p>
                      {!isCorrect && (
                        <p className="text-sm text-green-600">
                          Correct answer: {question.options[question.correct_answer]}
                        </p>
                      )}
                      <p className="text-sm mt-2 text-muted-foreground italic">
                        {question.explanation}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex gap-4 justify-center">
            {!passed && (
              <Button onClick={handleRetake}>
                Retake Quiz
              </Button>
            )}
            <Button variant="outline" onClick={() => window.history.back()}>
              Back to Module
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const question = questions[currentQuestion];
  const isLastQuestion = currentQuestion === questions.length - 1;
  const hasAnswered = selectedAnswers.has(currentQuestion);

  return (
    <Card className="p-8">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <span className="text-sm text-muted-foreground">
            Question {currentQuestion + 1} of {questions.length}
          </span>
          <span className="text-sm text-muted-foreground">
            Pass mark: {quiz.pass_mark || 80}%
          </span>
        </div>
        <div className="w-full bg-secondary h-2 rounded-full">
          <div
            className="bg-primary h-2 rounded-full transition-all"
            style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      <h3 className="text-xl font-semibold mb-6">{question.question}</h3>

      <RadioGroup
        value={selectedAnswers.get(currentQuestion)?.toString()}
        onValueChange={(value) => handleAnswerSelect(currentQuestion, parseInt(value))}
      >
        <div className="space-y-4">
          {question.options.map((option, index) => (
            <div key={index} className="flex items-center space-x-2">
              <RadioGroupItem value={index.toString()} id={`option-${index}`} />
              <Label htmlFor={`option-${index}`} className="cursor-pointer flex-1">
                {option}
              </Label>
            </div>
          ))}
        </div>
      </RadioGroup>

      <div className="mt-8 flex justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
          disabled={currentQuestion === 0}
        >
          Previous
        </Button>

        {isLastQuestion ? (
          <Button
            onClick={handleSubmit}
            disabled={selectedAnswers.size !== questions.length}
          >
            Submit Quiz
          </Button>
        ) : (
          <Button
            onClick={() => setCurrentQuestion(currentQuestion + 1)}
            disabled={!hasAnswered}
          >
            Next
          </Button>
        )}
      </div>

      <p className="text-center text-sm text-muted-foreground mt-4">
        {selectedAnswers.size} of {questions.length} questions answered
      </p>
    </Card>
  );
};

export default QuizPlayer;
